import React from 'react'
import { SearchResultsInterface } from 'common/types/entities/search-results-interface'
import ColorPicker from 'client/components/core/Sidebar/components/Settings/components/ColorPicker'
import TextFontUpdater from 'client/components/core/Sidebar/components/Settings/components/Font/TextFontUpdater'
import { RangeElement } from 'src/editor/shared/ui-lib/range-element'
import {
  useUpdateEntity,
  useUpdateNestedProp,
  useUpdateProp,
} from 'client/components/core/Sidebar/components/Settings/hooks/useUpdateProps'
import HtmlAttrId from 'client/components/core/Sidebar/components/Settings/options/HtmlAttrId'
import {
  AlignStyle,
  FontSize,
} from 'client/components/core/Sidebar/components/Settings/styles'
import TextLineHeight from 'client/components/core/Sidebar/components/Settings/styles/TextLineHeight'
import { useDeviceModeContext } from 'client/context/DeviceModeContext'
import DeviceAppearanceNew from '../../options/DeviceAppearance/DeviceAppearanceNew'
import { GroupTitle } from 'src/editor/layout/sidebar/settings/group-title'
import Border from '../../styles/Border'
import { SizeAndPositionGroup } from 'src/editor/shared/settings/groups/size-and-position'
import { MarginGroup } from 'src/editor/shared/settings/groups/margin'
import { PaddingGroup } from 'src/editor/shared/settings/groups/padding'

interface SearchResultsSettingsProps {
  entity: SearchResultsInterface
}

export const SearchResultsSettings = ({
  entity,
}: SearchResultsSettingsProps) => {
  const updateEntity = useUpdateEntity<SearchResultsInterface>()
  const updateProp = useUpdateProp(entity)
  const updateNestedProp = useUpdateNestedProp(entity)
  const { isMobile } = useDeviceModeContext()

  return (
    <>
      <RangeElement
        label="entity_settings.search_results.maximum_results_per_page"
        onChange={updateProp('maxItems')}
        value={entity.maxItems}
        allowNegative={false}
      />
      {/* card settings */}
      <GroupTitle>entity_settings.search_results.card_settings</GroupTitle>
      <ColorPicker
        update={updateNestedProp('card')('backgroundColor')}
        mobileUpdate={updateNestedProp('card')('mobileBackgroundColor')}
        color={entity.card?.backgroundColor}
        mobileColor={entity.card?.mobileBackgroundColor}
        label="settings_styles.background_color.label"
      />
      <Border
        showGroupTitle={false}
        update={updateNestedProp('card')('border')}
        mobileUpdate={updateNestedProp('card')('mobileBorder')}
        border={entity.card?.border}
        mobileBorder={entity.card?.mobileBorder}
      />
      <PaddingGroup
        padding={isMobile ? entity.card?.mobilePadding : entity.card?.padding}
        update={updateNestedProp('card')(
          isMobile ? 'mobilePadding' : 'padding',
        )}
        label="settings_styles.padding.label"
      />
      {/* total results text */}
      <GroupTitle>entity_settings.search_results.total_results_text</GroupTitle>
      <FontSize
        update={updateNestedProp('total')('fontSize')}
        mobileUpdate={updateNestedProp('total')('mobileFontSize')}
        fontSize={entity.total.fontSize}
        mobileFontSize={entity.total.mobileFontSize}
      />
      <TextLineHeight
        fontSize={entity.total.fontSize}
        mobileFontSize={entity.total.mobileFontSize}
        update={updateNestedProp('total')('lineHeight')}
        mobileUpdate={updateNestedProp('total')('mobileLineHeight')}
        lineHeight={entity.total.lineHeight}
        mobileLineHeight={entity.total.mobileLineHeight}
      />
      test ----
      <TextFontUpdater<SearchResultsInterface>
        entity={entity}
        update={updateEntity}
        nestedKey="total"
      />
      <AlignStyle
        align={isMobile ? entity.total.mobileTextAlign : entity.total.textAlign}
        update={updateNestedProp('total')(
          isMobile ? 'mobileTextAlign' : 'textAlign',
        )}
        isFlex={false}
      />
      <ColorPicker
        update={updateNestedProp('total')('color')}
        mobileUpdate={updateNestedProp('total')('mobileColor')}
        color={entity.total.color}
        mobileColor={entity.total.mobileColor}
      />
      <MarginGroup
        label="settings_styles.margin.label"
        margin={isMobile ? entity.total.mobileMargin : entity.total.margin}
        update={updateNestedProp('total')(isMobile ? 'mobileMargin' : 'margin')}
      />
      {/* card title */}
      <GroupTitle>entity_settings.search_results.card_title</GroupTitle>
      <FontSize
        update={updateNestedProp('title')('fontSize')}
        mobileUpdate={updateNestedProp('title')('mobileFontSize')}
        fontSize={entity.title.fontSize}
        mobileFontSize={entity.title.mobileFontSize}
      />
      <TextLineHeight
        fontSize={entity.title.fontSize}
        mobileFontSize={entity.title.mobileFontSize}
        update={updateNestedProp('title')('lineHeight')}
        mobileUpdate={updateNestedProp('title')('mobileLineHeight')}
        lineHeight={entity.title.lineHeight}
        mobileLineHeight={entity.title.mobileLineHeight}
      />
      <TextFontUpdater<SearchResultsInterface>
        entity={entity}
        update={updateEntity}
        nestedKey="title"
      />
      <AlignStyle
        align={isMobile ? entity.title.mobileTextAlign : entity.title.textAlign}
        update={updateNestedProp('title')(
          isMobile ? 'mobileTextAlign' : 'textAlign',
        )}
        isFlex={false}
      />
      <ColorPicker
        update={updateNestedProp('title')('color')}
        mobileUpdate={updateNestedProp('title')('mobileColor')}
        color={entity.title.color}
        mobileColor={entity.title.mobileColor}
      />
      <MarginGroup
        label="settings_styles.margin.label"
        margin={isMobile ? entity.title.mobileMargin : entity.title.margin}
        update={updateNestedProp('title')(isMobile ? 'mobileMargin' : 'margin')}
      />
      {/* card text */}
      <GroupTitle>entity_settings.search_results.card_text</GroupTitle>
      <FontSize
        update={updateNestedProp('text')('fontSize')}
        mobileUpdate={updateNestedProp('text')('mobileFontSize')}
        fontSize={entity.text.fontSize}
        mobileFontSize={entity.text.mobileFontSize}
      />
      <TextLineHeight
        fontSize={entity.text.fontSize}
        mobileFontSize={entity.text.mobileFontSize}
        update={updateNestedProp('text')('lineHeight')}
        mobileUpdate={updateNestedProp('text')('mobileLineHeight')}
        lineHeight={entity.text.lineHeight}
        mobileLineHeight={entity.text.mobileLineHeight}
      />
      <TextFontUpdater<SearchResultsInterface>
        entity={entity}
        update={updateEntity}
        nestedKey="text"
      />
      <AlignStyle
        align={isMobile ? entity.text.mobileTextAlign : entity.text.textAlign}
        update={updateNestedProp('text')(
          isMobile ? 'mobileTextAlign' : 'textAlign',
        )}
        isFlex={false}
      />
      <ColorPicker
        update={updateNestedProp('text')('color')}
        mobileUpdate={updateNestedProp('text')('mobileColor')}
        color={entity.text.color}
        mobileColor={entity.text.mobileColor}
      />
      <MarginGroup
        label="settings_styles.margin.label"
        margin={isMobile ? entity.text.mobileMargin : entity.text.margin}
        update={updateNestedProp('text')(isMobile ? 'mobileMargin' : 'margin')}
      />
      {/* card date */}
      <GroupTitle>entity_settings.search_results.card_date</GroupTitle>
      <FontSize
        update={updateNestedProp('date')('fontSize')}
        mobileUpdate={updateNestedProp('date')('mobileFontSize')}
        fontSize={entity.date.fontSize}
        mobileFontSize={entity.date.mobileFontSize}
      />
      <TextLineHeight
        fontSize={entity.date.fontSize}
        mobileFontSize={entity.date.mobileFontSize}
        update={updateNestedProp('date')('lineHeight')}
        mobileUpdate={updateNestedProp('date')('mobileLineHeight')}
        lineHeight={entity.date.lineHeight}
        mobileLineHeight={entity.date.mobileLineHeight}
      />
      <TextFontUpdater<SearchResultsInterface>
        entity={entity}
        update={updateEntity}
        nestedKey="date"
      />
      <AlignStyle
        align={isMobile ? entity.date.mobileTextAlign : entity.date.textAlign}
        update={updateNestedProp('date')(
          isMobile ? 'mobileTextAlign' : 'textAlign',
        )}
        isFlex={false}
      />
      <ColorPicker
        update={updateNestedProp('date')('color')}
        mobileUpdate={updateNestedProp('date')('mobileColor')}
        color={entity.date.color}
        mobileColor={entity.date.mobileColor}
      />
      <MarginGroup
        label="settings_styles.margin.label"
        margin={isMobile ? entity.date.mobileMargin : entity.date.margin}
        update={updateNestedProp('date')(isMobile ? 'mobileMargin' : 'margin')}
      />
      {/* pagination */}
      <GroupTitle>entity_settings.search_results.pagination</GroupTitle>
      <ColorPicker
        update={updateNestedProp('pagination')('activeColor')}
        mobileUpdate={updateNestedProp('pagination')('mobileActiveColor')}
        color={entity.pagination.activeColor}
        mobileColor={entity.pagination.mobileActiveColor}
        label="entity_settings.blog_post_listing.pagination.active_color"
      />
      <ColorPicker
        update={updateNestedProp('pagination')('activeBackgroundColor')}
        mobileUpdate={updateNestedProp('pagination')(
          'mobileActiveBackgroundColor',
        )}
        color={entity.pagination.activeBackgroundColor}
        mobileColor={entity.pagination.mobileActiveBackgroundColor}
        label="entity_settings.blog_post_listing.pagination.active_background_color"
      />
      <ColorPicker
        update={updateNestedProp('pagination')('inactiveColor')}
        mobileUpdate={updateNestedProp('pagination')('mobileInactiveColor')}
        color={entity.pagination.inactiveColor}
        mobileColor={entity.pagination.mobileInactiveColor}
        label="entity_settings.blog_post_listing.pagination.non_active_color"
      />
      <ColorPicker
        update={updateNestedProp('pagination')('inactiveBackgroundColor')}
        mobileUpdate={updateNestedProp('pagination')(
          'mobileInactiveBackgroundColor',
        )}
        color={entity.pagination.inactiveBackgroundColor}
        mobileColor={entity.pagination.mobileInactiveBackgroundColor}
        label="entity_settings.blog_post_listing.pagination.non_active_background_color"
      />
      <SizeAndPositionGroup
        padding={isMobile ? entity?.mobilePadding : entity.padding}
        updatePadding={updateProp(isMobile ? 'mobilePadding' : 'padding')}
        margin={isMobile ? entity.mobileMargin : entity.margin}
        updateMargin={updateProp(isMobile ? 'mobileMargin' : 'margin')}
      />
      <DeviceAppearanceNew entity={entity} />
      <HtmlAttrId attrId={entity.htmlAttrId} />
    </>
  )
}
